/**
 * StageManager - Orchestrates hot-swapping of stage assets
 *
 * Manages a single persistent scene with shared infrastructure (ground, walls, lighting)
 * and allows dynamic loading/unloading of stage-specific assets.
 *
 * Key responsibilities:
 * - Create and maintain shared assets (ground, walls, camera, lighting)
 * - Load stage packs (createAssets + physics + interactions)
 * - Clear previous stage before loading new one
 * - Emit events for stage lifecycle
 */

import {
  ArcRotateCamera,
  Color3,
  Color4,
  HemisphericLight,
  type Mesh,
  MeshBuilder,
  type Scene,
  Vector3,
} from "@babylonjs/core";
import { GridMaterial } from "@babylonjs/materials";
import { applySandboxCameraDefaults } from "../../utils/applySandboxCameraDefaults.js";
import { BaseManager } from "../core/BaseManager.js";
import type { InteractionManager } from "../interaction/InteractionManager.js";
import type { PhysicsManager } from "../physics/PhysicsManager.js";
import type {
  SharedAssets,
  StageLoadedEvent,
  StagePack,
  StageThemeInfo,
  StageUnloadedEvent,
} from "./types.js";

/**
 * Manager responsible for hot-swapping stage assets within a persistent scene
 */
export class StageManager extends BaseManager {
  private scene: Scene | null = null;
  private physicsManager: PhysicsManager | null = null;
  private interactionManager: InteractionManager | null = null;

  private currentPack: StagePack | null = null;
  private stageMeshes: Mesh[] = [];
  private sharedAssets: SharedAssets | null = null;

  private currentTheme: StageThemeInfo | null = null;

  // Guard against concurrent loads of the same pack (React StrictMode double-render race)
  private loadingPackId: string | null = null;

  /**
   * Initialize the manager
   */
  protected async onInitialize(): Promise<void> {
    // No async initialization needed - scene setup happens in setScene
  }

  /**
   * Dispose the manager and cleanup resources
   */
  protected onDispose(): void {
    // Clear current stage
    this.clearStageSync();

    // Dispose shared assets
    if (this.sharedAssets) {
      this.sharedAssets.ground.dispose();
      for (const wall of this.sharedAssets.walls) {
        wall.dispose();
      }
      this.sharedAssets = null;
    }

    this.scene = null;
    this.physicsManager = null;
    this.interactionManager = null;
    this.currentPack = null;
    this.stageMeshes = [];
  }

  /**
   * Set the scene reference
   */
  setScene(scene: Scene): void {
    this.scene = scene;
  }

  /**
   * Get the current scene
   */
  getScene(): Scene | null {
    return this.scene;
  }

  /**
   * Set the physics manager reference
   */
  setPhysicsManager(physicsManager: PhysicsManager): void {
    this.physicsManager = physicsManager;
  }

  /**
   * Set the interaction manager reference
   */
  setInteractionManager(interactionManager: InteractionManager): void {
    this.interactionManager = interactionManager;
  }

  /**
   * Get the current stage pack ID
   */
  getCurrentPackId(): string | null {
    return this.currentPack?.id ?? null;
  }

  /**
   * Get the current stage meshes
   */
  getStageMeshes(): Mesh[] {
    return [...this.stageMeshes];
  }

  /**
   * Load a stage pack into the scene
   *
   * This will:
   * 1. Clear the current stage
   * 2. Create shared assets if first load
   * 3. Update physics gravity
   * 4. Create stage-specific meshes
   * 5. Apply theme if set
   */
  async loadPack(pack: StagePack): Promise<void> {
    this.ensureInitialized();

    if (!this.scene) {
      throw new Error("Scene not set. Call setScene() first.");
    }

    if (!this.physicsManager) {
      throw new Error(
        "PhysicsManager not set. Call setPhysicsManager() first.",
      );
    }

    if (!this.interactionManager) {
      throw new Error(
        "InteractionManager not set. Call setInteractionManager() first.",
      );
    }

    // Skip if same pack is already loaded
    if (this.currentPack?.id === pack.id) {
      return;
    }

    // Skip if this pack is currently being loaded (race condition guard)
    if (this.loadingPackId === pack.id) {
      console.log(`[StageManager] SKIP_DUPLICATE_LOAD | id=${pack.id}`);
      return;
    }

    // Mark as loading to prevent concurrent loads
    this.loadingPackId = pack.id;

    console.log(`[StageManager] LOADING_PACK | id=${pack.id}`);

    // 1. Clear current stage (keep shared assets)
    await this.clearStage();

    // 2. Create shared assets if first load
    if (!this.sharedAssets) {
      this.sharedAssets = this.createSharedAssets();
      await this.setupSharedPhysics();
    }

    // 3. Update physics gravity
    this.physicsManager.setGravity(pack.physicsConfig.gravity);

    // 4. Create stage-specific meshes
    this.stageMeshes = await pack.createAssets({
      interactionManager: this.interactionManager,
      physicsManager: this.physicsManager,
      scene: this.scene,
    });

    // Mark meshes as physics roots for interaction
    for (const mesh of this.stageMeshes) {
      mesh.metadata = { ...mesh.metadata, isPhysicsRoot: true };
    }

    // 5. Configure interaction
    if (pack.interactionConfig.selectionColor) {
      this.interactionManager.setSelectionColor(
        pack.interactionConfig.selectionColor,
      );
    }
    if (pack.interactionConfig.hoverEffects !== undefined) {
      this.interactionManager.setHoverEffects({
        edgeRenderer: pack.interactionConfig.hoverEffects,
      });
    }
    if (pack.interactionConfig.selectionEffects !== undefined) {
      this.interactionManager.setSelectionEffects({
        halo: pack.interactionConfig.selectionEffects,
      });
    }

    // 6. Apply theme if set
    if (this.currentTheme && pack.applyTheme) {
      pack.applyTheme(this.stageMeshes, this.currentTheme);
    }

    this.currentPack = pack;
    this.loadingPackId = null; // Clear loading guard

    const event: StageLoadedEvent = {
      meshCount: this.stageMeshes.length,
      packId: pack.id,
    };
    this.emit("stageLoaded", event);

    console.log(
      `[StageManager] PACK_LOADED | id=${pack.id}, meshes=${this.stageMeshes.length}`,
    );
  }

  /**
   * Insert a stage pack without clearing existing meshes
   * Used for "insert" mode where meshes accumulate
   */
  async insertPack(pack: StagePack): Promise<void> {
    this.ensureInitialized();

    if (!this.scene) {
      throw new Error("Scene not set. Call setScene() first.");
    }

    if (!this.physicsManager) {
      throw new Error(
        "PhysicsManager not set. Call setPhysicsManager() first.",
      );
    }

    if (!this.interactionManager) {
      throw new Error(
        "InteractionManager not set. Call setInteractionManager() first.",
      );
    }

    console.log(`[StageManager] INSERTING_PACK | id=${pack.id}`);

    // Create shared assets if first load
    if (!this.sharedAssets) {
      this.sharedAssets = this.createSharedAssets();
      await this.setupSharedPhysics();
    }

    // Update physics gravity to match pack
    this.physicsManager.setGravity(pack.physicsConfig.gravity);

    // Create new meshes and ADD to existing array (don't replace)
    const newMeshes = await pack.createAssets({
      interactionManager: this.interactionManager,
      physicsManager: this.physicsManager,
      scene: this.scene,
    });

    // Mark meshes as physics roots for interaction
    for (const mesh of newMeshes) {
      mesh.metadata = { ...mesh.metadata, isPhysicsRoot: true };
    }

    // Append to tracked meshes (don't replace)
    this.stageMeshes.push(...newMeshes);

    // Configure interaction (use last pack's settings)
    if (pack.interactionConfig.selectionColor) {
      this.interactionManager.setSelectionColor(
        pack.interactionConfig.selectionColor,
      );
    }
    if (pack.interactionConfig.hoverEffects !== undefined) {
      this.interactionManager.setHoverEffects({
        edgeRenderer: pack.interactionConfig.hoverEffects,
      });
    }
    if (pack.interactionConfig.selectionEffects !== undefined) {
      this.interactionManager.setSelectionEffects({
        halo: pack.interactionConfig.selectionEffects,
      });
    }

    // Apply theme if set
    if (this.currentTheme && pack.applyTheme) {
      pack.applyTheme(newMeshes, this.currentTheme);
    }

    // Track last loaded pack (for theme updates)
    this.currentPack = pack;

    const event: StageLoadedEvent = {
      meshCount: newMeshes.length,
      packId: pack.id,
    };
    this.emit("stageLoaded", event);

    console.log(
      `[StageManager] PACK_INSERTED | id=${pack.id}, new=${newMeshes.length}, total=${this.stageMeshes.length}`,
    );
  }

  /**
   * Clear the current stage
   */
  async clearStage(): Promise<void> {
    this.clearStageSync();
  }

  /**
   * Synchronous version of clearStage for use in dispose
   */
  private clearStageSync(): void {
    if (!this.currentPack) {
      return;
    }

    const packId = this.currentPack.id;
    console.log(`[StageManager] CLEARING_STAGE | id=${packId}`);

    // Call optional cleanup
    this.currentPack.onUnload?.();

    // Clear interaction state (selection and hover effects)
    if (this.interactionManager) {
      this.interactionManager.clearAllSelections();
      this.interactionManager.clearHover();
    }

    // Remove physics bodies and dispose meshes
    if (this.physicsManager) {
      for (const mesh of this.stageMeshes) {
        const meshId = mesh.id || mesh.name;
        this.physicsManager.removePhysicsBody(meshId);
      }
    }

    // Dispose meshes (with children and materials)
    // doNotRecurse=false means DO recurse through children and dispose them
    for (const mesh of this.stageMeshes) {
      mesh.dispose(false, true);
    }
    this.stageMeshes = [];

    const event: StageUnloadedEvent = { packId };
    this.emit("stageUnloaded", event);

    this.currentPack = null;
    console.log(`[StageManager] STAGE_CLEARED | id=${packId}`);
  }

  /**
   * Set the current theme and apply to stage meshes
   */
  setTheme(theme: StageThemeInfo): void {
    this.currentTheme = theme;

    // Apply to current stage if loaded
    if (this.currentPack?.applyTheme && this.stageMeshes.length > 0) {
      this.currentPack.applyTheme(this.stageMeshes, theme);
    }
  }

  /**
   * Create shared assets (ground, walls, camera, lighting)
   * These persist across stage changes
   */
  private createSharedAssets(): SharedAssets {
    if (!this.scene) {
      throw new Error("Scene not set");
    }

    const scene = this.scene;

    // Setup camera
    this.setupCamera(scene);

    // Setup lighting
    const light = new HemisphericLight("light", new Vector3(0, 1, 0), scene);
    light.intensity = 0.9;

    // Set scene background color
    scene.clearColor = new Color4(0.08, 0.08, 0.1, 1);

    // Create ground with grid material
    const ground = MeshBuilder.CreateGround(
      "ground",
      { height: 57, width: 57 },
      scene,
    );
    const groundMaterial = new GridMaterial("groundMat", scene);
    groundMaterial.majorUnitFrequency = 5;
    groundMaterial.minorUnitVisibility = 0.3;
    groundMaterial.gridRatio = 1;
    groundMaterial.mainColor = new Color3(0.15, 0.15, 0.18);
    groundMaterial.lineColor = new Color3(0.3, 0.3, 0.35);
    groundMaterial.opacity = 0.98;
    ground.material = groundMaterial;
    ground.position.y = 0;

    // Create invisible boundary walls
    const wallHeight = 10;
    const wallThickness = 0.5;
    const groundSize = 28.5; // Half of ground size (57/2)

    const wallPositions = [
      { name: "wallNorth", rotY: 0, x: 0, z: groundSize },
      { name: "wallSouth", rotY: 0, x: 0, z: -groundSize },
      { name: "wallEast", rotY: Math.PI / 2, x: groundSize, z: 0 },
      { name: "wallWest", rotY: Math.PI / 2, x: -groundSize, z: 0 },
    ];

    const walls: Mesh[] = [];
    for (const wp of wallPositions) {
      const wall = MeshBuilder.CreateBox(
        wp.name,
        { depth: wallThickness, height: wallHeight, width: 57 },
        scene,
      );
      wall.position.set(wp.x, wallHeight / 2, wp.z);
      wall.rotation.y = wp.rotY;
      wall.visibility = 0; // Invisible but still has physics
      wall.isPickable = false; // Don't intercept pointer events
      walls.push(wall);
    }

    console.log("[StageManager] SHARED_ASSETS_CREATED");

    return { ground, walls };
  }

  /**
   * Setup the camera with WASD panning and QEZC rotation
   */
  private setupCamera(scene: Scene): void {
    // Dispose default camera if exists
    const defaultCamera = scene.getCameraByName("defaultCamera");
    if (defaultCamera) {
      defaultCamera.detachControl();
      defaultCamera.dispose();
    }

    const camera = new ArcRotateCamera(
      "stageCamera",
      Math.PI / 4, // alpha: horizontal angle
      Math.PI / 3, // beta: vertical angle (60° from top)
      12, // radius
      new Vector3(0, 1, 0), // target
      scene,
    );

    // Set limits BEFORE attaching controls
    camera.lowerRadiusLimit = 5;
    camera.upperRadiusLimit = 25;
    camera.lowerBetaLimit = 0.1;
    camera.upperBetaLimit = Math.PI / 2 - 0.05;
    camera.minZ = 0.1;

    // QEZC for orbit rotation
    camera.keysLeft = [81]; // Q - rotate left
    camera.keysRight = [69]; // E - rotate right
    camera.keysUp = [90]; // Z - tilt up
    camera.keysDown = [67]; // C - tilt down

    const canvas = scene.getEngine().getRenderingCanvas();
    if (canvas) {
      camera.attachControl(canvas, true);
    }

    // WASD panning
    const panSpeed = 0.3;
    const keysPressed = new Set<string>();

    scene.onKeyboardObservable.add((kbInfo) => {
      const key = kbInfo.event.key.toLowerCase();
      if (kbInfo.type === 1) {
        // KeyDown
        keysPressed.add(key);
      } else if (kbInfo.type === 2) {
        // KeyUp
        keysPressed.delete(key);
      }
    });

    scene.onBeforeRenderObservable.add(() => {
      if (keysPressed.size === 0) return;

      const forward = camera.getForwardRay().direction;
      const forwardXZ = new Vector3(forward.x, 0, forward.z).normalize();
      const rightXZ = Vector3.Cross(Vector3.Up(), forwardXZ).normalize();

      const panDelta = Vector3.Zero();
      if (keysPressed.has("w") || keysPressed.has("arrowup")) {
        panDelta.addInPlace(forwardXZ.scale(panSpeed));
      }
      if (keysPressed.has("s") || keysPressed.has("arrowdown")) {
        panDelta.addInPlace(forwardXZ.scale(-panSpeed));
      }
      if (keysPressed.has("a") || keysPressed.has("arrowleft")) {
        panDelta.addInPlace(rightXZ.scale(-panSpeed));
      }
      if (keysPressed.has("d") || keysPressed.has("arrowright")) {
        panDelta.addInPlace(rightXZ.scale(panSpeed));
      }

      if (panDelta.length() > 0) {
        camera.target.addInPlace(panDelta);
      }
    });

    // Apply sandbox zoom/pinch defaults (skip framing — arena camera has fixed setup)
    applySandboxCameraDefaults(camera, scene, { skipFramingBehavior: true });

    console.log("[StageManager] CAMERA_SETUP");
  }

  /**
   * Add physics to shared assets
   */
  private async setupSharedPhysics(): Promise<void> {
    if (!this.physicsManager || !this.sharedAssets) {
      return;
    }

    const { ground, walls } = this.sharedAssets;

    // Add ground physics (static) - thick floor prevents tunneling
    this.physicsManager.addPhysicsBody(ground, {
      center: { x: 0, y: -2.5, z: 0 }, // Shift down so top surface at Y=0
      extents: { x: 19, y: 5, z: 19 }, // 5 units thick
      friction: 0.5,
      mass: 0, // Static
      restitution: 0.3,
      shapeType: "BOX",
    });

    // Add physics to invisible walls (static, low bounce)
    for (const wall of walls) {
      this.physicsManager.addPhysicsBody(wall, {
        friction: 0.5,
        mass: 0, // Static
        restitution: 0.1, // Low bounce off walls
        shapeType: "BOX",
      });
    }

    console.log("[StageManager] SHARED_PHYSICS_SETUP");
  }
}
